import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    Stack,
    Select,
    MenuItem,
    CircularProgress,
    type SelectChangeEvent,
    Alert,
    type Theme,
    styled,
    useMediaQuery,
    useTheme,
    DialogActions,
    DialogContent
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import InvitationBackground from '../InvitationBackground.jpg';
import InvitationBackgroundSmall from '../InvitationBackgroundSmall.jpg';
const InviteContainer = styled('div')(({ theme }: { theme: Theme }) => ({
    minWidth: '100%',
    minHeight: '886px',
    paddingTop: '50px',
    backgroundImage: `url(${InvitationBackground})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
    [theme.breakpoints.up('md')]: {
        minWidth: '670px'
    },
    [theme.breakpoints.down('sm')]: {
        backgroundImage: `url(${InvitationBackgroundSmall})`
    }
}));

const EmailTextField = styled(TextField)(({ theme }: { theme: Theme }) => ({
    minWidth: '100%',
    maxWidth: '350px'
}));

export function InvitationPage() {
    const [params] = useSearchParams();
    const emailFromParams = params.get('email') || '';
    const [email, setEmail] = useState(emailFromParams);
    const [emailFromInput, setEmailFromInput] = useState(emailFromParams);
    const [names, setNames] = useState('');
    const [badEmailError, setBadEmailError] = useState<string>('');
    const [songRequest, setSongRequest] = useState('');
    const [guests, setGuests] = useState(1);
    const [maxGuests, setMaxGuests] = useState(1);
    const [dietaryRestrictions, setDietaryRestrictions] = useState('');
    const [submitErrorMessage, setSubmitErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isAddEmailDialogOpen, setIsAddEmailDialogOpen] = useState(!email);
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchNames = useCallback(async (email: string) => {
        try {
            const res = await fetch('https://get-invitee-info-646286843763.us-central1.run.app', {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email
                })
            });
            const body = await res.json();
            if (body.error === 'email not found') {
                setIsAddEmailDialogOpen(true);
                setBadEmailError(
                    'Whoops. Email not found. Please use the email of the Save The Date receiver. Thanks.'
                );
                setNames('');
                return;
            }
            const maxGuests = body.maxGuests;
            const alredySubmittedGuests = body.guests ? Number(body.guests) : maxGuests;
            setNames(body.names);
            setGuests(alredySubmittedGuests);
            setDietaryRestrictions(body.dietaryRestrictions || '');
            setMaxGuests(maxGuests);
            setSongRequest(body.songs || '');
            if (body.guests) {
                setAlreadySubmitted(true);
            }
        } catch (error) {
            console.log('----ERROR----', error);
        }
    }, []);

    useEffect(() => {
        if (email) {
            setSubmitErrorMessage('');
            setIsAddEmailDialogOpen(false);
            setBadEmailError('');
            fetchNames(email);
        } else {
            setIsAddEmailDialogOpen(true);
            setNames('');
        }
    }, [email]);

    const onSubmit = async (declined = false) => {
        if (!email) {
            setSubmitErrorMessage('Please fill in your email.');
            setIsAddEmailDialogOpen(true);
            return;
        }

        setIsSubmitting(true);

        const res = await fetch('https://accept-invitation-646286843763.us-central1.run.app', {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                email,
                numOfGuests: declined ? 0 : guests,
                songRequest: declined ? '' : songRequest,
                dietaryRestrictions: declined ? '' : dietaryRestrictions
            })
        });

        setIsSubmitting(false);

        if (!res.ok) {
            const body = await res.json();
            if (body.error === 'email not found') {
                setSubmitErrorMessage('Whoops. Something went wrong. Please check your response and try again.');
            }
        } else {
            setSuccessMsg('Submitted successfully! Thanks for letting us know.');
        }
    };

    function handleGuestsChange(event: SelectChangeEvent<number>) {
        const guests = Number(event.target.value);
        if (guests <= maxGuests && guests >= 0) {
            setGuests(guests);
        }
    }

    const declined = guests === 0;

    const guestOptions = [];
    for (let i = 0; i < maxGuests + 1; i++) {
        guestOptions.push(
            <MenuItem sx={{ color: 'black' }} key={i} value={i}>
                {i}
            </MenuItem>
        );
    }

    return (
        <Stack alignItems="center" justifyContent="center">
            <InviteContainer>
                {!names && !badEmailError && (
                    <Stack width="100%" height="30rem" alignItems={'center'} justifyContent={'center'}>
                        <CircularProgress />
                    </Stack>
                )}
                <Dialog open={isAddEmailDialogOpen} onClose={() => setIsAddEmailDialogOpen(false)}>
                    <DialogContent>
                        <p style={{ padding: '10px' }}>Enter the email the invite was sent to</p>
                        {badEmailError && <p style={{ padding: '10px' }}>{badEmailError}</p>}
                        <EmailTextField
                            placeholder="Email"
                            value={emailFromInput}
                            onChange={(e) => setEmailFromInput(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            style={{ backgroundColor: 'black', color: 'white' }}
                            onClick={() => setEmail(emailFromInput)}
                        >
                            Submit
                        </Button>
                    </DialogActions>
                </Dialog>

                {!names && badEmailError && (
                    <Stack width="100%" height="30rem" alignItems={'center'} justifyContent={'center'}>
                        <Grid
                            container
                            spacing={2}
                            direction="column"
                            paddingTop={6}
                            paddingBottom={10}
                            paddingLeft={isSmallScreen ? '35px' : '0px'}
                            paddingRight={isSmallScreen ? '60px' : '0px'}
                            alignItems="center"
                        >
                            <Typography
                                sx={{
                                    fontSize: 'xx-large',
                                    padding: '0.7rem 1.3rem',
                                    letterSpacing: '8px'
                                }}
                                variant="body1"
                                textAlign="center"
                                width={isSmallScreen ? '100%' : '400px'}
                            >
                                Could not find invitee
                            </Typography>
                        </Grid>
                    </Stack>
                )}
                {names && (
                    <Grid
                        container
                        spacing={2}
                        direction="column"
                        paddingTop={6}
                        paddingBottom={10}
                        paddingLeft={isSmallScreen ? '35px' : '0px'}
                        paddingRight={isSmallScreen ? '60px' : '0px'}
                        alignItems="center"
                    >
                        <Typography
                            sx={{
                                fontSize: 'xx-large',
                                padding: '0.7rem 1.3rem',
                                letterSpacing: '8px'
                            }}
                            variant="body1"
                            textAlign="center"
                            width={isSmallScreen ? '100%' : '400px'}
                        >
                            TO: {names.toUpperCase()}
                        </Typography>
                        <Stack
                            sx={{
                                borderRadius: '10px',
                                padding: '1rem'
                            }}
                        >
                            <Typography
                                variant="body1"
                                textAlign="center"
                                width={isSmallScreen ? '100%' : '300px'}
                                sx={{ fontSize: 'medium' }}
                            >
                                CALVIN AND BECKY ARE PLEASED TO INVITE YOU TO OUR WEDDING ON
                                <br /> <br />{' '}
                                <span style={{ fontSize: '1.6rem', color: '#eae7e7', letterSpacing: '8px' }}>
                                    AUGUST 31ST 2025
                                </span>
                                <br />
                                <p style={{ fontFamily: 'baletta' }}>at</p>
                                <span style={{ fontSize: '1.6rem', color: '#eae7e7' }}>
                                    THE RIVER BARN WILDERNESS TOURS
                                </span>
                                <p style={{ fontFamily: 'baletta' }}>at</p>
                                <span style={{ fontSize: '1.6rem', color: '#eae7e7' }}>4PM</span>
                            </Typography>
                        </Stack>
                        <Stack
                            direction="column"
                            alignItems={'center'}
                            justifyContent={'center'}
                            spacing={1}
                            width={'100%'}
                            sx={{
                                padding: '0.7rem'
                            }}
                        >
                            <Stack direction="row" spacing={1} alignItems="center" justifyContent={'center'}>
                                <Typography>Number of guests attending dinner</Typography>
                                <Select
                                    value={guests}
                                    onChange={handleGuestsChange}
                                    name="guestsCount"
                                    size="small"
                                    variant="outlined"
                                    sx={{ width: '80px', backgroundColor: 'white' }}
                                >
                                    {guestOptions}
                                </Select>
                            </Stack>
                            {!declined && (
                                <Stack width={'70%'} spacing={1} alignItems="center" justifyContent={'center'}>
                                    <Typography>Dietary Restrictions</Typography>
                                    <TextField
                                        placeholder="Any dietary needs we should be aware of?"
                                        value={dietaryRestrictions}
                                        name="dietaryRestrictions"
                                        onChange={(e) => setDietaryRestrictions(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                    />
                                </Stack>
                            )}
                            {!declined && (
                                <Stack width={'70%'} spacing={1} alignItems="center" justifyContent={'center'}>
                                    <Typography>Song requests</Typography>
                                    <TextField
                                        placeholder="Song requests"
                                        value={songRequest}
                                        name="songRequest"
                                        onChange={(e) => setSongRequest(e.target.value)}
                                        variant="outlined"
                                        fullWidth
                                    />
                                </Stack>
                            )}
                        </Stack>
                        <Stack spacing={1} direction={'row'} alignItems="center" justifyContent={'center'}>
                            <Button
                                color="error"
                                sx={{ backgroundColor: '#303233' }}
                                variant="contained"
                                onClick={() => onSubmit(true)}
                            >
                                {isSubmitting ? 'Submitting...' : 'Decline'}
                            </Button>
                            <Button
                                sx={{
                                    color: '#303233'
                                }}
                                color="primary"
                                variant="contained"
                                onClick={() => onSubmit()}
                            >
                                {isSubmitting ? 'Submitting...' : 'Accept'}
                            </Button>
                        </Stack>
                        {(alreadySubmitted || successMsg) && (
                            <Stack sx={{ width: '100%' }} alignItems="center">
                                <Alert sx={{ maxWidth: '300px' }} severity="success">
                                    {successMsg || 'Thank you for already responding'}
                                </Alert>
                            </Stack>
                        )}
                        {submitErrorMessage && (
                            <Stack sx={{ width: '100%' }} alignItems="center">
                                <Alert sx={{ maxWidth: '300px' }} severity="error">
                                    {submitErrorMessage}
                                </Alert>
                            </Stack>
                        )}
                    </Grid>
                )}
            </InviteContainer>
        </Stack>
    );
}
